import React, {
  useEffect,
  useContext,
  ReactNode,
  useMemo,
  useState,
} from 'react';
import { Hub } from 'aws-amplify/utils';
import { signInWithRedirect, signOut } from 'aws-amplify/auth';
import useCurrentUser from '../hooks/useCurrentUser.ts';
import { User } from '../types/user.ts';

interface AuthContextType {
  user?: User;
  isAuthenticating: boolean;
  login: () => void;
  signup: () => void;
  logout: () => void;
}

const AuthContext = React.createContext<AuthContextType>(null!);

// Define Cognito domain and client ID based on your configuration
const COGNITO_DOMAIN = 'https://auth.capsuletime.co';
const CLIENT_ID = '1pakgui8lic8n3l6naqr2mg7c0';

function AuthProvider({ children }: { children: ReactNode }) {
  const [hasLoggedOut, setHasLoggedOut] = useState(false);
  const { data, isLoading } = useCurrentUser(hasLoggedOut);

  useEffect(() => {
    const listener = Hub.listen('auth', ({ payload: { event } }) => {
      switch (event) {
        case 'tokenRefresh_failure':
          window.location.reload();
          break;
        default:
          break;
      }
    });
    return () => listener();
  }, []);

  const login = () => {
    signInWithRedirect();
  };

  const generateRandomString = (length: number) => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  };

  // Function to create SHA-256 hash and convert to base64url
  const generateCodeChallenge = async (codeVerifier: string) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  };

  const signup = async () => {
    try {
      // Generate required PKCE parameters
      const codeVerifier = generateRandomString(43);
      const state = generateRandomString(32);
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      
      // Store code verifier and state in localStorage for validation later
      localStorage.setItem('pkce_code_verifier', codeVerifier);
      localStorage.setItem('pkce_state', state);
      
      // Construct signup URL with all required parameters
      const redirectUri = encodeURIComponent(window.location.origin);
      const scopes = encodeURIComponent('email openid phone profile');
      
      const signupUrl = 
        `${COGNITO_DOMAIN}/signup?` +
        `redirect_uri=${redirectUri}&` +
        `response_type=code&` +
        `client_id=${CLIENT_ID}&` +
        `scope=${scopes}&` +
        `state=${state}&` +
        `code_challenge=${encodeURIComponent(codeChallenge)}&` +
        `code_challenge_method=S256`;
      
      // Redirect to the signup page
      window.location.href = signupUrl;
    } catch (error) {
      console.error('Error in signup redirect:', error);
      // Fallback to regular login if there's an error
      signInWithRedirect();
    }
  };

  const logout = async () => {
    await signOut();
    setHasLoggedOut(true);
  };

  const value = useMemo(
    () => ({
      user: data,
      isAuthenticating: isLoading,
      login,
      signup,
      logout,
    }),
    [data, isLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthProvider;

export const useAuth = () => {
  return useContext(AuthContext);
};